import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { SORTING_ANCHOR } from '../../util/faqAnchors';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import sharedCss from '../_marketingPage/marketingPage.module.css';
import css from './FaqPage.module.css';

// Topic groups for the FAQ page. Each entry drives both a TOC link
// and the rendered section below. Questions are referenced by a small
// integer index that maps onto translation keys:
//   `FaqPage.{topicKey}q{n}Question` / `FaqPage.{topicKey}q{n}Answer`
const TOPICS = [
  { id: 'om-zuprize', topicKey: 't1', questionCount: 4 },
  // Account section was 6 Q's in the original brief; we merged the
  // three "Stripe onboarding for buyers" entries into one accurate
  // question, leaving 4 total. (Buyers don't go through Stripe
  // onboarding — that's a seller-only flow.)
  { id: 'konto', topicKey: 't2', questionCount: 4 },
  // q6 is also linked on its own, from the search page and the footer.
  { id: 'kjop', topicKey: 't3', questionCount: 6, anchors: { 6: SORTING_ANCHOR } },
  { id: 'betaling', topicKey: 't4', questionCount: 4 },
  { id: 'frakt', topicKey: 't5', questionCount: 6 },
  { id: 'reklamasjon', topicKey: 't6', questionCount: 9 },
  { id: 'salg', topicKey: 't7', questionCount: 3 },
  { id: 'tillit', topicKey: 't8', questionCount: 4 },
  { id: 'kontakt', topicKey: 't9', questionCount: 1 },
];

/**
 * FAQ page (`/faq`).
 *
 * Long-form question-and-answer page covering the eight topics in the
 * client's May 2026 FAQ brief (About, Account, Buying, Payment,
 * Shipping, Claims, Selling, Trust) plus a Contact section. Content
 * owned by the translation files; the component is a thin renderer
 * that walks the `TOPICS` table.
 *
 * Two technical corrections to the client's source doc (decided
 * during build, to be flagged back to the client):
 *  - The three buyer-side "Stripe onboarding" Q's were merged into a
 *    single accurate Q. Buyers don't onboard with Stripe — they just
 *    pay, with an optional save-card. Stripe Connect onboarding is
 *    seller-only.
 *  - "Contact us through chat or email" was reduced to "email". We
 *    don't run a live-chat widget; email is the only support channel.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const FaqPageComponent = props => {
  const intl = useIntl();
  // "Annonser fra private selgere og Zuprize Partnere…": one key shared by this answer and Terms
  // §15, so a change to that sentence lands in both.
  const equalTreatmentText = intl.formatMessage({ id: 'Sorting.equalTreatment' });
  const { scrollingDisabled } = props;

  const supportEmail = 'post@zuprize.no';
  const emailLink = (
    <ExternalLink href={`mailto:${supportEmail}`} className={sharedCss.kontaktLink}>
      {supportEmail}
    </ExternalLink>
  );
  const br = <br />;

  return (
    <Page
      title={intl.formatMessage({ id: 'FaqPage.metaTitle' })}
      description={intl.formatMessage({ id: 'FaqPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="FaqPage" />}
        footer={<FooterContainer />}
      >
        <article className={sharedCss.root}>
          <div className={sharedCss.content}>
            <header className={sharedCss.hero}>
              <p className={sharedCss.heroEyebrow}>
                <FormattedMessage id="FaqPage.heroEyebrow" />
              </p>
              <h1 className={sharedCss.heroHeading}>
                <FormattedMessage id="FaqPage.heroHeading" />
              </h1>
              <p className={sharedCss.lede}>
                <FormattedMessage id="FaqPage.heroLede" />
              </p>
              <p className={sharedCss.lastUpdated}>
                <FormattedMessage id="FaqPage.lastUpdated" />
              </p>
            </header>

            <nav className={css.toc} aria-labelledby="faqToc">
              <p id="faqToc" className={css.tocEyebrow}>
                <FormattedMessage id="FaqPage.tocEyebrow" />
              </p>
              <ul className={css.tocList}>
                {TOPICS.map(topic => (
                  <li key={topic.id} className={css.tocItem}>
                    <a href={`#${topic.id}`} className={css.tocLink}>
                      <FormattedMessage id={`FaqPage.${topic.topicKey}Label`} />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {TOPICS.map(topic => (
              <section
                key={topic.id}
                id={topic.id}
                className={classNames(sharedCss.section, css.topicSection)}
              >
                <p className={sharedCss.sectionEyebrow}>
                  <FormattedMessage id={`FaqPage.${topic.topicKey}Eyebrow`} />
                </p>
                {Array.from({ length: topic.questionCount }, (_, i) => i + 1).map(n => (
                  <div
                    key={n}
                    id={topic.anchors?.[n]}
                    className={topic.anchors?.[n] ? classNames(css.qa, css.anchoredQa) : css.qa}
                  >
                    <p className={css.question}>
                      <FormattedMessage id={`FaqPage.${topic.topicKey}q${n}Question`} />
                    </p>
                    <p className={css.answer}>
                      <FormattedMessage
                        id={`FaqPage.${topic.topicKey}q${n}Answer`}
                        values={{ email: emailLink, equalTreatment: equalTreatmentText }}
                      />
                    </p>
                  </div>
                ))}
              </section>
            ))}
          </div>

          <section className={sharedCss.kontakt}>
            <div className={sharedCss.kontaktInner}>
              <p className={sharedCss.kontaktEyebrow}>
                <FormattedMessage id="FaqPage.kontaktEyebrow" />
              </p>
              <p className={sharedCss.kontaktBody}>
                <FormattedMessage id="FaqPage.kontaktBody" values={{ br, email: emailLink }} />
              </p>
            </div>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const FaqPage = connect(mapStateToProps)(FaqPageComponent);

export { FaqPageComponent };
export default FaqPage;
